import { FC, useState } from 'react';

import { MessageMedium } from 'constants/messageMediums';
import { selectUserProjectToken } from 'core/userContext';
import { selectFeatureFlags } from 'core/userContext/selectors';
import { useQuerySavedRowCount } from 'modules/dragAndDropEditor/hooks/useQuerySavedRowCount';
import { addNegativeToast, addPositiveToast } from 'modules/toastContainer';
import { useDispatch, useSelector } from 'react-redux';
import { localizeCompactDateTime } from 'utils/l10n';
import { ToastMessage } from '../../components';
import {
  CREATE_SAVED_ROW_ERROR_MESSAGE,
  CREATE_SAVED_ROW_SUCCESS_MESSAGE,
  DELETE_SAVED_ROW_ERROR_MESSAGE,
  DELETE_SAVED_ROW_SUCCESS_MESSAGE,
  QUERY_BEE_TOKEN_ERROR_MESSAGE,
  QUERY_MERGE_PARAMS_ERROR_MESSAGE,
  QUERY_MESSAGE_CATEGORIES_ERROR_MESSAGE,
  UPDATE_SAVED_ROW_ERROR_MESSAGE,
  UPDATE_SAVED_ROW_SUCCESS_MESSAGE,
} from '../../constants';
import {
  useDragAndDropEditorTranslations,
  useQueryBeeAuthToken,
  useQueryMergeParams,
  useTrackDragAndDropEditorRUMError,
} from '../../hooks';
import { useCreateSavedRowMutation } from '../../hooks/useCreateSavedRowMutation';
import { useDeleteSavedRowMutation } from '../../hooks/useDeleteSavedRowMutation';
import { useQueryMessageCategories } from '../../hooks/useQueryMessageCategories';
import { useQuerySavedRows } from '../../hooks/useQuerySavedRows';
import { useSimulateParseBeeRowQuery } from '../../hooks/useSimulateParseBeeRowQuery';
import { useUpdateSavedRowMutation } from '../../hooks/useUpdateSavedRowMutation';
import {
  CreateSavedRowResponse,
  DeleteSavedRowResponse,
  DragAndDropEditorError,
  DragAndDropEditorProps,
  UpdateSavedRowResponse,
} from '../../types';
import {
  createSavedRowFailureToErrorMessage,
  deleteSavedRowFailureToErrorMessage,
  isAuthTokenDefined,
  isAuthTokenUnexpired,
} from '../../utils';
import { useDragAndDropEditorModalsContext } from '../DragAndDropEditorModalsContext';
import {
  DragAndDropEditorContext,
  DragAndDropEditorContextState,
} from './DragAndDropEditorContext';

type DragAndDropEditorProviderProps = Pick<
  DragAndDropEditorProps,
  'messageMedium' | 'editSavedRowDesign' | 'hasUnsavedTemplateChanges'
>;

export const DragAndDropEditorProvider: FC<
  React.PropsWithChildren<DragAndDropEditorProviderProps>
> = ({ children, messageMedium, editSavedRowDesign, hasUnsavedTemplateChanges }) => {
  const [isSavingTemplateForSavedRow, setIsSavingTemplateForSavedRow] = useState(false); // This is used to track the process of saving a template before switching to Edit Single Row mode.

  const dispatch = useDispatch();
  const { t } = useDragAndDropEditorTranslations();

  const projectUID = useSelector(selectUserProjectToken);
  const trackError = useTrackDragAndDropEditorRUMError();
  const { closeAllModals } = useDragAndDropEditorModalsContext();

  const {
    BeePopupBuilderMigration: isBeePopupBuilderMigrationEnabled,
    SavedRows: isSavedRowsFeatureEnabled,
  } = useSelector(selectFeatureFlags);

  const isPopupBuilderSupportedMedium = messageMedium === MessageMedium.InApp;
  const usePopupBuilder = !!(isBeePopupBuilderMigrationEnabled && isPopupBuilderSupportedMedium);

  const {
    error: authTokenError,
    isLoading: isFetchingAuthToken,
    token: authToken,
    getBeeAuthToken,
  } = useQueryBeeAuthToken(usePopupBuilder, {
    onSuccess(token) {
      const isInvalid = isAuthTokenDefined(token) && !isAuthTokenUnexpired(token);

      if (isInvalid) {
        const errorMessage = t('toast.invalidToken', {
          computerTime: localizeCompactDateTime(),
          tokenExp: localizeCompactDateTime(new Date(token?.['.expires'] as string)),
        });

        dispatch(addNegativeToast(errorMessage));
        trackError({
          token,
          errorMessage,
          errorName: DragAndDropEditorError.DRAG_AND_DROP_EDITOR_INVALID_TOKEN_ERROR,
        });
      }
    },
    onError(error) {
      dispatch(addNegativeToast(QUERY_BEE_TOKEN_ERROR_MESSAGE, error));
      trackError({ error, errorMessage: QUERY_BEE_TOKEN_ERROR_MESSAGE });
    },
  });

  const {
    mergeParams,
    isLoading: isFetchingMergeParams,
    error: mergeParamsError,
    getMergeParams,
  } = useQueryMergeParams(messageMedium, {
    onError(error: any) {
      dispatch(addNegativeToast(QUERY_MERGE_PARAMS_ERROR_MESSAGE, error));
      trackError({ error, errorMessage: QUERY_MERGE_PARAMS_ERROR_MESSAGE });
    },
  });

  const {
    messageCategories,
    refetch: getMessageCategories,
    isLoading: isFetchingCategories,
  } = useQueryMessageCategories({
    skip: !isSavedRowsFeatureEnabled,
    onError(error: any) {
      dispatch(addNegativeToast(QUERY_MESSAGE_CATEGORIES_ERROR_MESSAGE, error));
      trackError({ error, errorMessage: QUERY_MESSAGE_CATEGORIES_ERROR_MESSAGE });
    },
  });

  const {
    createSavedRow,
    isCreatingSavedRow,
    failure,
    reset: resetCreateSavedRow,
  } = useCreateSavedRowMutation({
    onCompleted: (data: CreateSavedRowResponse) => {
      const _failure = data?.createBeeRow?.failure;

      if (_failure) {
        const message = createSavedRowFailureToErrorMessage(_failure);
        dispatch(addNegativeToast(message, data));
      } else {
        dispatch(
          addPositiveToast(
            <ToastMessage
              name={data?.createBeeRow?.beeRowName ?? ''}
              message={CREATE_SAVED_ROW_SUCCESS_MESSAGE}
            />,
          ),
        );
        closeAllModals();
        getMessageCategories();
        resetCreateSavedRow();
      }
    },
    onError: (error: any) => {
      dispatch(addNegativeToast(CREATE_SAVED_ROW_ERROR_MESSAGE, error));
      trackError({ error, errorMessage: CREATE_SAVED_ROW_ERROR_MESSAGE });
    },
  });

  const {
    updateSavedRow,
    reset: resetUpdateSavedRow,
    isUpdatingSavedRow,
    failure: updateSavedRowFailure,
  } = useUpdateSavedRowMutation({
    onCompleted: (data: UpdateSavedRowResponse) => {
      const _failure = data?.updateBeeRow?.failure;

      if (_failure) {
        const message = createSavedRowFailureToErrorMessage(_failure);
        dispatch(addNegativeToast(message, data));
      } else {
        dispatch(
          addPositiveToast(
            <ToastMessage
              name={data?.updateBeeRow?.beeRowName ?? ''}
              message={UPDATE_SAVED_ROW_SUCCESS_MESSAGE}
            />,
          ),
        );
        closeAllModals();
        getMessageCategories();
        resetUpdateSavedRow();
      }
    },
    onError: (error: any) => {
      dispatch(addNegativeToast(UPDATE_SAVED_ROW_ERROR_MESSAGE, error));
      trackError({ error, errorMessage: UPDATE_SAVED_ROW_ERROR_MESSAGE });
    },
  });

  const {
    deleteSavedRow,
    isDeletingSavedRow,
    failure: deleteSavedRowFailure,
  } = useDeleteSavedRowMutation({
    onCompleted: (data: DeleteSavedRowResponse) => {
      const _failure = data?.deleteBeeRow?.failure;

      if (_failure) {
        const message = deleteSavedRowFailureToErrorMessage(_failure);
        dispatch(addNegativeToast(message, data));
      } else {
        dispatch(
          addPositiveToast(
            <ToastMessage
              name={data?.deleteBeeRow?.beeRowName ?? ''}
              message={DELETE_SAVED_ROW_SUCCESS_MESSAGE}
            />,
          ),
        );
      }
    },
    onError: (error: any) => {
      dispatch(addNegativeToast(DELETE_SAVED_ROW_ERROR_MESSAGE, error));
      trackError({ error, errorMessage: DELETE_SAVED_ROW_ERROR_MESSAGE });
    },
  });

  const {
    savedRows,
    isLoading: isFetchingSavedRows,
    error: savedRowsError,
    getSavedRows,
  } = useQuerySavedRows();

  const { getSavedRowCount, savedRowCount } = useQuerySavedRowCount();

  const parseSavedRowProcess = useSimulateParseBeeRowQuery();

  const value: DragAndDropEditorContextState = {
    authToken,
    authTokenError,
    getBeeAuthToken,
    getMergeParams,
    isFetchingAuthToken,
    isFetchingMergeParams,
    mergeParams,
    mergeParamsError,
    projectUID,
    messageMedium,
    trackError,
    messageCategories,
    isFetchingCategories,
    getMessageCategories,
    createSavedRow,
    isCreatingSavedRow,
    createSavedRowFailure: failure,
    createSavedRowReset: resetCreateSavedRow,
    deleteSavedRow,
    isDeletingSavedRow,
    deleteSavedRowFailure,
    savedRows,
    isFetchingSavedRows,
    savedRowsError,
    getSavedRows,
    updateSavedRow,
    resetUpdateSavedRow,
    isUpdatingSavedRow,
    updateSavedRowFailure,
    parseSavedRowProcess,
    getSavedRowCount,
    savedRowCount,
    editSavedRowDesign,
    hasUnsavedTemplateChanges,
    isSavingTemplateForSavedRow,
    setIsSavingTemplateForSavedRow,
  };

  return (
    <DragAndDropEditorContext.Provider value={value}>{children}</DragAndDropEditorContext.Provider>
  );
};
